import { addBreadcrumb } from '@sentry/react';

const STORAGE_KEY = 'officeDialog:active';

let activeDialog: Office.Dialog | null = null;
let lastCloseTime = 0;

// ─── Stale-dialog recovery ────────────────────────────────────────────────────
// If the taskpane reloads (HMR, crash, navigation) while a dialog is open,
// the module-level `activeDialog` reference is lost but Office still holds the
// slot. Persisting a flag in sessionStorage lets us detect this on the next
// module init and apply a settling delay before the first open attempt.
if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(STORAGE_KEY)) {
  addBreadcrumb({
    message:
      '[OfficeDialog] detected stale dialog flag in sessionStorage — a dialog was open when the module last unloaded. Treating as just-closed to force settling delay.',
    type: 'warning',
  });
  lastCloseTime = Date.now();
  sessionStorage.removeItem(STORAGE_KEY);
}

const REOPEN_DELAY_MS = 1500;
const RETRY_DELAYS_MS = [1000, 2000, 3000];

// ─── Sentinel errors ──────────────────────────────────────────────────────────
/**
 * Thrown when all displayDialogAsync retries fail with 12007 and we never
 * held a reference to the blocking dialog (i.e. it is truly orphaned).
 * Callers can instanceof-check this to show a targeted user-facing message.
 */
export class OrphanedDialogError extends Error {
  constructor() {
    super('An Office dialog is already open. Please close it and try again.');
    this.name = 'OrphanedDialogError';
  }
}

/**
 * The dialog never opened because something blocked it, not because anything
 * is broken:
 *  - 12009: the user chose **Ignore** on Outlook on the web's "allow this
 *    add-in to show a dialog" prompt.
 *  - 12011: the browser itself blocks pop-ups (a Safari configuration, or a
 *    site-level block), so the host could not open the window at all.
 *
 * Both are fixed by the user, not by us, and neither is worth retrying — the
 * next attempt is blocked the same way until they act.
 */
export class DialogBlockedError extends Error {
  /** The host's displayDialogAsync error code, for messaging and Sentry. */
  readonly code: number;

  constructor(code: number) {
    super(
      code === DIALOG_ERROR_IGNORED_BY_USER
        ? 'Outlook blocked the sign-in window.'
        : 'The browser blocked the sign-in window.',
    );
    this.name = 'DialogBlockedError';
    this.code = code;
  }
}

/** displayDialogAsync error codes we handle by name rather than by number. */
const DIALOG_ERROR_SLOT_OCCUPIED = 12_007;
const DIALOG_ERROR_IGNORED_BY_USER = 12_009;
const DIALOG_ERROR_POPUP_BLOCKED = 12_011;

/**
 * Closes the currently active Office dialog, if any.
 */
export function closeOfficeDialog(): void {
  if (activeDialog) {
    try {
      activeDialog.close();
    } catch {
      addBreadcrumb({
        message: 'dialog.close() failed — already closed',
      });
    }
    activeDialog = null;
    lastCloseTime = Date.now();
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

/**
 * Marks the dialog as closed without calling close() — use this when the
 * dialog has already closed itself (e.g. after receiving a message from it).
 * Records lastCloseTime so the reopen-delay logic still applies.
 */
export function markDialogClosed(): void {
  activeDialog = null;
  sessionStorage.removeItem(STORAGE_KEY);
  // lastCloseTime is intentionally NOT set here — the caller must still call
  // dialog.close() and then update lastCloseTime via closeOfficeDialog() or
  // by calling recordDialogClosed() after close() returns.
}

/**
 * Records that a dialog.close() call has just been issued.
 * Call this immediately after dialog.close() to start the settling timer.
 */
export function recordDialogClosed(): void {
  lastCloseTime = Date.now();
}

export interface OfficeDialogOptions {
  displayInIframe?: boolean;
  height?: number;
  width?: number;
}

// ─── Main API ─────────────────────────────────────────────────────────────────

/**
 * Opens an Office dialog, closing any existing one first.
 * Registers a DialogEventReceived handler to track user-initiated closes.
 *
 * Key invariants:
 * - Options are cloned on every displayDialogAsync attempt to prevent Office.js
 *   from mutating the shared object across retries (which would cause the
 *   "Callback cannot be specified both in argument list and in optional object"
 *   error on the second attempt).
 * - A settling delay is applied after any close before re-opening, because
 *   Office's dialog subsystem is asynchronous and close() is fire-and-forget.
 * - If all retries fail with 12007 and we never held a reference to the
 *   blocking dialog, an OrphanedDialogError is thrown so callers can surface
 *   an actionable message to the user.
 */
export async function openOfficeDialog(
  url: string,
  options: OfficeDialogOptions = {},
): Promise<Office.Dialog> {
  const hadActiveDialogOnEntry = !!activeDialog;
  closeOfficeDialog();
  // Wait for Office to fully release the dialog slot after a prior close.
  const elapsed = Date.now() - lastCloseTime;

  if (lastCloseTime > 0 && elapsed < REOPEN_DELAY_MS) {
    const delay = REOPEN_DELAY_MS - elapsed;
    await new Promise((r) => setTimeout(r, delay));
  }

  return new Promise((resolve, reject) => {
    let retryIndex = 0;

    function attempt() {
      // Clone options on every attempt — Office.js mutates the options object
      // it receives (writes the callback into it). Reusing the same reference
      // on a retry would leave a stale callback property, triggering:
      // "Callback cannot be specified both in argument list and in optional object"
      const attemptOptions = { ...options };

      Office.context.ui.displayDialogAsync(url, attemptOptions, (asyncResult) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          addBreadcrumb({
            message: `[OfficeDialog] displayDialogAsync failed — code=${asyncResult.error.code}, message="${asyncResult.error.message}", retryIndex=${retryIndex}`,
            type: 'error',
          });

          // Blocked before it opened — by the user (12009) or by the browser
          // (12011). Retrying just hits the same block, so surface it as its
          // own error with copy that tells them what to change.
          if (
            asyncResult.error.code === DIALOG_ERROR_IGNORED_BY_USER ||
            asyncResult.error.code === DIALOG_ERROR_POPUP_BLOCKED
          ) {
            reject(new DialogBlockedError(asyncResult.error.code));
            return;
          }

          // Error 12007: dialog slot still occupied — retry with backoff.
          if (
            asyncResult.error.code === DIALOG_ERROR_SLOT_OCCUPIED &&
            retryIndex < RETRY_DELAYS_MS.length
          ) {
            const retryDelay = RETRY_DELAYS_MS[retryIndex++];
            setTimeout(attempt, retryDelay);
            return;
          }

          // All retries exhausted (or non-12007 error).
          if (asyncResult.error.code === DIALOG_ERROR_SLOT_OCCUPIED) {
            // We never held a reference to the blocking dialog — it is orphaned
            // (e.g. stale from a previous taskpane reload). Only throw
            // OrphanedDialogError when we didn't close a dialog ourselves; if
            // we did close one (`hadActiveDialogOnEntry`), the error is still
            // Office being slow so we use the generic message.
            if (!hadActiveDialogOnEntry) {
              addBreadcrumb({
                message:
                  '[OfficeDialog] all retries exhausted on 12007 with no known active dialog — orphaned dialog detected',
                type: 'error',
              });
              reject(new OrphanedDialogError());
              return;
            }
          }

          reject(new Error(asyncResult.error.message));
          return;
        }

        const dialog = asyncResult.value;
        activeDialog = dialog;
        sessionStorage.setItem(STORAGE_KEY, '1');

        // Track user-initiated close (e.g. clicking X on the dialog window).
        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          activeDialog = null;
          lastCloseTime = Date.now();
          sessionStorage.removeItem(STORAGE_KEY);
        });

        resolve(dialog);
      });
    }

    attempt();
  });
}
